<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';

const store = useStore();
const router = useRouter();
const route = useRoute();

const isVisible = ref(false);
const currentAlert = ref(null);
const waitTime = ref('00:00');
let timer = null;

const highestPriorityAlert = computed(() => store.getters['alertQueue/highestPriorityAlert']);

const formatWaitTime = (waitingSince) => {
  if (!waitingSince) return '00:00';
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - waitingSince * 1000) / 1000);
  
  if (diffInSeconds < 0) return '00:00';
  
  const totalMinutes = Math.floor(diffInSeconds / 60);
  const seconds = diffInSeconds % 60;
  
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  
  return `${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const startTimer = () => {
  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    if (currentAlert.value) {
      waitTime.value = formatWaitTime(currentAlert.value.conversation.waiting_since);
    }
  }, 1000);
};

const isUserTypingGlobally = () => {
  if (!window.alertQueueGlobalTypingAt) return false;
  return (Date.now() - window.alertQueueGlobalTypingAt) < 60000;
};

const checkAndShowAlert = () => {
  const newAlert = highestPriorityAlert.value;
  
  if (!newAlert) {
    if (isVisible.value) closeAlert();
    return;
  }

  const isCurrentConversation = 
    route.name === 'inbox_conversation' && 
    Number(route.params.conversation_id) === Number(newAlert.conversation.id);

  if (newAlert.show_popup && !isCurrentConversation && !isUserTypingGlobally()) {
    // Only set if not already visible for the SAME conversation
    if (!isVisible.value || currentAlert.value?.conversation?.id !== newAlert.conversation.id) {
      currentAlert.value = newAlert;
      waitTime.value = formatWaitTime(newAlert.conversation.waiting_since);
      isVisible.value = true;
      startTimer();
    }
  } else if (isCurrentConversation && isVisible.value && currentAlert.value?.conversation?.id === newAlert.conversation.id) {
    closeAlert();
  } else if (isUserTypingGlobally() && isVisible.value) {
    closeAlert();
  }
};

import { onMounted, onUnmounted } from 'vue';

let activeCheckInterval = null;

onMounted(() => {
  activeCheckInterval = setInterval(checkAndShowAlert, 1000);
});

onUnmounted(() => {
  if (activeCheckInterval) clearInterval(activeCheckInterval);
});

// Remove old watch since setInterval handles the logic now
// (It handles both property updates and typing timeout asynchronously)

watch(() => route.params.conversation_id, (newId) => {
  if (isVisible.value && currentAlert.value && Number(newId) === Number(currentAlert.value.conversation.id)) {
    closeAlert();
  }
});

const closeAlert = () => {
  isVisible.value = false;
  currentAlert.value = null;
  if (timer) clearInterval(timer);
};

const goToConversation = () => {
  if (!currentAlert.value) return;
  const conversation = currentAlert.value.conversation;
  const accountId = store.getters.getCurrentAccountId;
  
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId,
      inbox_id: conversation.inbox_id || 0, // Fallback as it might not be in payload
      conversation_id: conversation.id
    }
  });
  closeAlert();
};

const suppressAlert = async () => {
  if (!currentAlert.value) return;
  const conversationId = currentAlert.value.conversation.id;
  const alertRuleId = currentAlert.value.alert_rule.id;
  
  await store.dispatch('alertQueue/suppressAlert', { conversationId, alertRuleId });
};

const contactName = computed(() => {
  return currentAlert.value?.conversation?.contact_name || 'Cliente';
});

const alertRuleColor = computed(() => {
  return currentAlert.value?.alert_rule?.color || '#FF3B30';
});

const alertRuleName = computed(() => {
  return currentAlert.value?.alert_rule?.name || 'Alerta';
});

const allowSnooze = computed(() => {
  return currentAlert.value?.alert_rule?.allow_snooze === true;
});

const lastMessageContent = computed(() => {
  return currentAlert.value?.conversation?.last_message_content || '';
});

</script>

<template>
  <div v-if="isVisible" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm dark:bg-black/60 transition-opacity">
    <div class="relative w-full max-w-md overflow-hidden bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-8 flex flex-col items-center transform transition-all">
      
      <!-- Fundo Colorido do Alerta -->
      <div 
        class="absolute top-0 w-full h-32 opacity-20 pointer-events-none"
        :style="{ background: `linear-gradient(to bottom, ${alertRuleColor}, transparent)` }"
      ></div>


      <!-- Ícone e Título -->
      <div class="flex flex-col items-center z-10 w-full">
        <div 
          class="w-20 h-20 mb-4 flex items-center justify-center rounded-full animate-pulse ring-8"
          :style="{ color: alertRuleColor, backgroundColor: `${alertRuleColor}20`, boxShadow: `0 0 0 8px ${alertRuleColor}10` }"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-10 h-10">
            <path d="M12 2.25a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0V3a.75.75 0 0 1 .75-.75ZM7.5 12a4.5 4.5 0 1 1 9 0v5.25a.75.75 0 0 1-.75.75h-7.5a.75.75 0 0 1-.75-.75V12Zm4.5-1.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM4.53 4.53a.75.75 0 0 1 1.06 0l1.06 1.06a.75.75 0 1 1-1.06 1.06L4.53 5.59a.75.75 0 0 1 0-1.06Zm14.94 0a.75.75 0 0 1 0 1.06l-1.06 1.06a.75.75 0 1 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0ZM3 12a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm18 0a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 21 12ZM4.5 19.5h15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-.75.75H4.5a.75.75 0 0 1-.75-.75v-1.5a.75.75 0 0 1 .75-.75Z" />
          </svg>
        </div>
        
        <span 
          class="px-3 py-1 text-[11px] font-bold tracking-widest rounded-full uppercase"
          :style="{ color: alertRuleColor, backgroundColor: `${alertRuleColor}20` }"
        >
          {{ alertRuleName }}
        </span>
        
        <h2 class="mt-4 text-2xl font-bold text-slate-800 dark:text-slate-100 text-center line-clamp-1 w-full px-4">{{ contactName }}</h2>
        
        <div class="mt-3 flex items-center justify-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-4 py-1.5 shadow-sm">
          <span class="w-2 h-2 rounded-full animate-ping" :style="{ backgroundColor: alertRuleColor }"></span>
          <span class="text-sm font-medium text-slate-600 dark:text-slate-300">Aguardando resposta há</span>
        </div>
        
        <div class="mt-4 text-6xl font-bold font-mono tracking-tighter drop-shadow-sm" :style="{ color: alertRuleColor }">
          {{ waitTime }}
        </div>

        <!-- Última Mensagem -->
        <div v-if="lastMessageContent" class="mt-6 w-full bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-700/50 shadow-inner">
          <p class="text-xs font-semibold text-slate-400 dark:text-slate-500 mb-1.5 uppercase tracking-wider text-left flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
            </svg>
            Última mensagem
          </p>
          <p 
            class="text-sm text-slate-700 dark:text-slate-300 text-left line-clamp-3 italic cursor-default"
            :title="lastMessageContent"
          >
            "{{ lastMessageContent }}"
          </p>
        </div>
      </div>

      <!-- Botão Iniciar Atendimento -->
      <button 
        @click="goToConversation" 
        class="group w-full mt-8 px-6 py-4 text-base font-bold text-white uppercase tracking-wide transition-all rounded-xl shadow-lg flex items-center justify-center gap-2 hover:-translate-y-0.5"
        :style="{ backgroundColor: alertRuleColor, boxShadow: `0 4px 14px 0 ${alertRuleColor}60` }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
        </svg>
        Iniciar Atendimento
      </button>

      <!-- Botão Não Atender Agora -->
      <button 
        v-if="allowSnooze"
        @click="suppressAlert" 
        class="w-full mt-3 px-4 py-3 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        Não atender agora
      </button>

    </div>
  </div>
</template>
